import React, { useState } from "react";
import { Usb, Folder, FileText, Image as ImageIcon, ChevronRight, HardDrive, FolderInput, Loader2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { dapi as api } from "./deskApi";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";

const FM_LIMIT = 10 * 1024 * 1024;
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fileIcon = (name) => (/\.(png|jpe?g|gif|webp|bmp|heic)$/i.test(name) ? ImageIcon : FileText);

/** Browse a USB stick (or any folder) plugged into THIS computer and copy
 * its files into the File Manager. Uses the browser's File System Access
 * API — Chrome/Edge on desktop. */
export default function UsbApp() {
  const [stack, setStack] = useState([]); // [{name, handle}]
  const [entries, setEntries] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);

  const supported = typeof window.showDirectoryPicker === "function";

  const listDir = async (handle) => {
    const rows = [];
    for await (const e of handle.values()) rows.push({ name: e.name, kind: e.kind, handle: e });
    rows.sort((a, b) => (a.kind !== b.kind ? (a.kind === "directory" ? -1 : 1) : a.name.localeCompare(b.name)));
    return rows;
  };

  const openDrive = async () => {
    try {
      const handle = await window.showDirectoryPicker();
      setStack([{ name: handle.name || "Drive", handle }]);
      setEntries(await listDir(handle));
      setSel(new Set());
    } catch (e) {
      if (e?.name !== "AbortError") toast.error(e.message);
    }
  };

  const enter = async (entry) => {
    setStack((prev) => [...prev, { name: entry.name, handle: entry.handle }]);
    setEntries(await listDir(entry.handle));
    setSel(new Set());
  };

  const goTo = async (idx) => {
    const next = stack.slice(0, idx + 1);
    setStack(next);
    setEntries(await listDir(next[next.length - 1].handle));
    setSel(new Set());
  };

  const toggle = (name) => setSel((prev) => {
    const n = new Set(prev);
    if (n.has(name)) n.delete(name); else n.add(name);
    return n;
  });

  const ensureImportFolder = async () => {
    const { data } = await api.get("/desktop/fs");
    const found = (data.items || []).find((x) => x.type === "folder" && x.name === "USB imports");
    if (found) return found.id;
    const { data: f } = await api.post("/desktop/fs/folder", { name: "USB imports", parent_id: null });
    return f.id;
  };

  const importFiles = async (names) => {
    const targets = entries.filter((e) => e.kind === "file" && names.includes(e.name));
    if (!targets.length) return;
    setBusy(true);
    try {
      const folderId = await ensureImportFolder();
      let done = 0, skipped = 0;
      for (const t of targets) {
        setProgress({ done, total: targets.length, name: t.name });
        const file = await t.handle.getFile();
        if (file.size > FM_LIMIT) { skipped += 1; done += 1; continue; }
        const fd = new FormData();
        fd.append("file", file, file.name);
        fd.append("parent_id", folderId);
        await api.post("/desktop/fs/upload", fd);
        done += 1;
      }
      window.dispatchEvent(new CustomEvent("desk-fs-changed"));
      toast.success(`Imported ${done - skipped} file${done - skipped === 1 ? "" : "s"} into "USB imports".${skipped ? ` ${skipped} skipped (over 10MB).` : ""}`);
      setSel(new Set());
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const filesHere = entries.filter((e) => e.kind === "file");

  if (!supported) {
    return (
      <div className="h-full grid place-items-center bg-white p-6 text-center" data-testid="usb-app">
        <div className="max-w-sm">
          <Usb className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-600">
            This browser can't open USB drives. Please use <b>Chrome</b> or <b>Edge</b> on a desktop computer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white" data-testid="usb-app">
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-slate-50">
        <Button size="sm" className="rounded-sm bg-sky-700 hover:bg-sky-800" onClick={openDrive} data-testid="usb-open-drive">
          <HardDrive className="w-4 h-4 mr-2" /> {stack.length ? "Open another drive" : "Open USB drive / folder"}
        </Button>
        {stack.length > 1 && (
          <button type="button" onClick={() => goTo(stack.length - 2)} className="p-1.5 rounded hover:bg-slate-200 text-slate-600" title="Back" data-testid="usb-back">
            <ArrowLeft className="w-4 h-4" />
          </button>
        )}
        <div className="flex items-center text-xs text-slate-500 min-w-0 overflow-hidden">
          {stack.map((s, i) => (
            <span key={i} className="flex items-center shrink-0">
              {i > 0 && <ChevronRight className="w-3 h-3 mx-0.5" />}
              <button type="button" onClick={() => goTo(i)} className="hover:underline truncate max-w-[120px]">{s.name}</button>
            </span>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        {stack.length === 0 ? (
          <div className="h-full grid place-items-center text-center p-6">
            <div className="max-w-md">
              <Usb className="w-12 h-12 text-sky-300 mx-auto mb-3" />
              <h3 className="font-semibold text-slate-700">Plug your USB stick into this computer</h3>
              <p className="text-sm text-slate-500 mt-1.5">
                Then click <b>"Open USB drive / folder"</b> above and pick the drive in the window that appears.
                You can browse it and copy files straight into your File Manager.
              </p>
            </div>
          </div>
        ) : entries.length === 0 ? (
          <p className="text-sm text-slate-500 p-4">This folder is empty.</p>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="usb-file-list">
            {entries.map((e) => {
              const Icon = e.kind === "directory" ? Folder : fileIcon(e.name);
              return (
                <li key={e.name} className="flex items-center gap-2.5 px-3 py-2 hover:bg-slate-50 text-sm">
                  {e.kind === "file" ? (
                    <input type="checkbox" checked={sel.has(e.name)} onChange={() => toggle(e.name)} className="accent-sky-600" data-testid={`usb-check-${e.name}`} />
                  ) : <span className="w-[13px]" />}
                  <Icon className={`w-4 h-4 shrink-0 ${e.kind === "directory" ? "text-amber-500" : "text-slate-400"}`} />
                  {e.kind === "directory" ? (
                    <button type="button" onClick={() => enter(e)} className="truncate hover:underline text-left flex-1" data-testid={`usb-dir-${e.name}`}>{e.name}</button>
                  ) : (
                    <span className="truncate flex-1">{e.name}</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {stack.length > 0 && (
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-t bg-slate-50">
          <div className="text-xs text-slate-500">
            {progress ? `Copying ${progress.name}… (${progress.done + 1}/${progress.total})` : `${sel.size} of ${filesHere.length} file${filesHere.length === 1 ? "" : "s"} selected · files over 10MB are skipped`}
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" className="rounded-sm" disabled={busy || filesHere.length === 0}
              onClick={() => importFiles(filesHere.map((f) => f.name))} data-testid="usb-import-all">
              Import all here
            </Button>
            <Button size="sm" className="rounded-sm bg-sky-700 hover:bg-sky-800" disabled={busy || sel.size === 0}
              onClick={() => importFiles([...sel])} data-testid="usb-import-selected">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5 mr-1.5" />}
              Import selected
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
